import { Injectable } from '@angular/core';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { MatDialog as MatDialogMdc } from '@angular/material/dialog';
import { TwoFactorGateDialogComponent } from '../../components/v2-layout/shared/two-factor-gate-dialog/two-factor-gate-dialog.component';
import { TwoFactorGateReason } from '../../enums/two-factor-gate-reason.enum';
import { Observable, from, EMPTY } from 'rxjs';
import { switchMap, catchError, filter, take, finalize, tap } from 'rxjs/operators';
import { Routes } from '../../config/routes';
import { ApplicationHttpClient } from '../../components/shared/http/application-http-client';
import { TreasuryService } from './treasury.service';
import { LegFiJwtService } from '../auth/legfi-jwt.service';
import { BankAccountInsightsService } from '../financial-accounts/bank-account-insights.service';
import { GrowlerService } from '../../components/shared/growler/growler.service';
import { BankAccountsService } from '../financial-accounts/bank-accounts.service';
import {
    BankAccountKycDialogComponent,
    BankAccountKycDialogResult,
} from '../../components/app-layout/shared/bank-account-kyc-dialog/bank-account-kyc-dialog.component';
import {
    BankAccountStripeOnboardingDialogComponent,
    BankAccountStripeOnboardingDialogInput,
} from '../../components/app-layout/shared/bank-account-stripe-onboarding-dialog/bank-account-stripe-onboarding-dialog.component';
import { MatDialogSizes } from '../../enums/mat-dialog-sizes.enum';
import {
    ManualAccountEntryComponent,
    ManualEntryDialogInput,
    ManualEntryDialogResult,
} from '../../components/app-layout/shared/bank-account-manager/manual-account-entry/manual-account-entry.component';
import { ONBOARDING_STATUSES, TREASURY_VERIFICATION_BLOCKED_COPY } from '../../components/v2-layout/stripe-treasury/stripe-treasury.constants';
import { OrganizationBroadcastService, OrganizationEventType } from '../organization/organization-broadcast.service';

export interface FinancialConnectionsSessionResponse {
    clientSecret: string;
    sessionId: string;
    publishableKey: string;
    stripeAccount: string;
}

export interface ProcessAccountsRequest {
    accountIds: string[];
}

export interface ProcessedFcAccount {
    id: number;
    friendlyName: string;
    last4: string;
    routingNumber: string | null;
    sourceType: string;
    stripeFinancialConnectionsId: string;
    fcBalance: number | null;
}

export interface ProcessAccountsResponse {
    accounts: ProcessedFcAccount[];
}

/**
 * SD-9078. Details typed by the user after Stripe's Financial Connections modal declined to
 * return a linked account, which is what happens when they pick "enter details manually".
 */
export interface LinkManualAccountRequest {
    routingNumber: string;
    accountNumber: string;
    accountHolderType: 'company' | 'individual';
    /** Optional; the backend falls back to the organization's name for Stripe billing details. */
    accountHolderName?: string;
    friendlyName?: string;
}

/**
 * A Financial Connections result that completed with a session but linked nothing.
 *
 * SD-9078 review (spwoodall): there is NO way to tell a deliberate modal close from a manual-entry
 * completion. Stripe emits no cancellation signal for `collectFinancialConnectionsAccounts`:
 * `FinancialConnectionsSessionResult` is a two-arm union, `{financialConnectionsSession, error?:
 * undefined}` or `{financialConnectionsSession: undefined, error}` (stripe.d.ts:1344), with no
 * cancellation variant, and `FinancialConnectionsSession` itself carries only `id`, `accounts`,
 * `filters`, `permissions` and `return_url`: no status. `grep -r "session_cancelled"
 * node_modules/@stripe/stripe-js` returns zero hits on 7.9.0.
 *
 * An earlier revision of this file guarded on `error.code === 'session_cancelled'`. That code does
 * not exist, so the guard could never fire; it read as protection while providing none. Both a
 * close and a manual-entry completion arrive here as the FIRST arm with `accounts: []` and no
 * `error` at all, so this predicate names an AMBIGUOUS outcome, not a cancellation, and each caller
 * chooses a response that is honest under both readings.
 */
function completedWithNoAccounts(result: any): boolean {
    return !result?.error && (result?.financialConnectionsSession?.accounts ?? []).length === 0;
}

/**
 * The outcome of attaching a Financial Connections account to an existing bank account.
 *
 * SD-9824 added two fields beside `success`, which still means only "the link went through" and is
 * unchanged for existing readers. What it could never say is whether the account came out UN-FROZEN:
 * org 46726's admin got `success: true` and a balance that moved $647.58 -> $852.95, beside a
 * Reconnect prompt that stayed put.
 *
 * `connectionVerified: false` is NOT a failure and must not be shown as one — core refreshed the
 * balance but has not yet seen the succeeded TRANSACTION refresh its un-freeze requires.
 */
export interface LinkBalanceResult {
    /** The link itself went through: the FC account is stored and the balance refreshed. */
    success: boolean;
    /** The account is un-frozen — the Reconnect prompt is gone. Absent from a core predating SD-9824. */
    connectionVerified?: boolean;
    /** A background job is re-checking the connection and will clear the prompt if it recovered. */
    verificationPending?: boolean;
}

export interface LinkManualAccountResponse {
    account: {
        id: number;
        friendlyName: string;
        last4: string;
        routingNumber: string | null;
        sourceType: string;
        stripePaymentMethodId: string | null;
        /** Set when Stripe wants the two small deposits confirmed before it will move money. */
        microdepositVerificationRequiredAt: string | null;
    };
}

@Injectable({
    providedIn: 'root',
})
export class FinancialConnectionsService {
    constructor(
        private _http: ApplicationHttpClient,
        private _treasuryService: TreasuryService,
        private _growler: GrowlerService,
        private _dialog: MatDialog,
        private _gateDialog: MatDialogMdc,
        private _bankAccountsService: BankAccountsService,
        private _orgBroadcast: OrganizationBroadcastService,
        private _bankAccountInsights: BankAccountInsightsService,
    ) {
        // SD-6865 broadcast adoption: replaces `_pollUntilTreasuryAccountReady`.
        // Backend dispatches `treasury.financial-account.ready` from
        // `TreasuryWebhookSubscriber::handleFinancialAccountFeaturesUpdated`
        // when outbound_transfers transitions to active (same trigger as the
        // payout-destination registration job). One fetch on receipt is all
        // that's needed — `createStream=true` pushes to `$bankAccounts`, so
        // subscribed deposit-account selectors flip to the readonly "PayHOA
        // Balance" state automatically.
        this._orgBroadcast.onEventType$(OrganizationEventType.TREASURY_FINANCIAL_ACCOUNT_READY)
            .pipe(
                // Defensive: Echo channels are already scoped to
                // `Organization.{orgId}`, but drop replayed or malformed
                // payloads that do not match the current JWT's orgId.
                filter((event) => LegFiJwtService.read()?.orgId === event.organizationId),
                switchMap((event) =>
                    this._bankAccountsService.getOrgBankAccounts(event.organizationId, null, true).pipe(take(1)),
                ),
            )
            .subscribe();
    }

    /**
     * Creates a Financial Connections session for linking external bank accounts.
     * Returns a clientSecret to initialize the Stripe Financial Connections modal.
     */
    public createSession(organizationId: number): Observable<FinancialConnectionsSessionResponse> {
        const url = Routes.MakeLegFiCoreUrl(
            Routes.TreasuryModule.FinancialConnectionsSessions(organizationId),
        );
        return this._http.post<FinancialConnectionsSessionResponse>(url, '{}');
    }

    /**
     * Processes linked accounts after the user completes the Stripe Financial Connections flow.
     * Sends the selected account IDs (fca_xxx format) to the backend for persistence.
     */
    public processAccounts(
        organizationId: number,
        accountIds: string[],
    ): Observable<ProcessAccountsResponse> {
        const url = Routes.MakeLegFiCoreUrl(
            Routes.TreasuryModule.FinancialConnectionsAccounts(organizationId),
        );
        const body: ProcessAccountsRequest = { accountIds };
        return this._http.post<ProcessAccountsResponse>(url, JSON.stringify(body));
    }

    /**
     * SD-9078: persist a bank account from routing/account numbers the user typed.
     *
     * The counterpart to processAccounts(). A Financial Connections session completed by manual
     * entry returns no `fca_` account, so there are no ids to post — the numbers themselves are
     * all we have, and the backend mints the Stripe PaymentMethod from them.
     */
    public linkManualAccount(
        organizationId: number,
        details: LinkManualAccountRequest,
    ): Observable<LinkManualAccountResponse> {
        const url = Routes.MakeLegFiCoreUrl(
            Routes.TreasuryModule.FinancialConnectionsManualAccounts(organizationId),
        );
        return this._http.post<LinkManualAccountResponse>(url, JSON.stringify(details));
    }

    /**
     * Get the Stripe-provided default friendly name for an FC account.
     */
    public getDefaultName(
        organizationId: number,
        fcAccountId: string,
    ): Observable<{ defaultName: string }> {
        const url = Routes.MakeLegFiCoreUrl(
            Routes.TreasuryModule.FinancialConnectionsAccountDefaultName(organizationId, fcAccountId),
        );
        return this._http.get<{ defaultName: string }>(url);
    }

    /**
     * Trigger a manual balance refresh and sync for an FC account.
     */
    public syncBalance(
        organizationId: number,
        fcAccountId: string,
    ): Observable<{ success: boolean }> {
        const url = Routes.MakeLegFiCoreUrl(
            Routes.TreasuryModule.FinancialConnectionsAccountSyncBalance(organizationId, fcAccountId),
        );
        return this._http.post<{ success: boolean }>(url, '{}');
    }

    /**
     * Open the Stripe Financial Connections modal and collect accounts.
     * Loads Stripe.js dynamically and presents the FC modal to the user.
     */
    public async collectAccounts(session: FinancialConnectionsSessionResponse): Promise<any> {
        const { loadStripe } = await import('@stripe/stripe-js');
        const stripe = await loadStripe(session.publishableKey, {
            stripeAccount: session.stripeAccount,
        });

        if (!stripe) {
            throw new Error('Failed to load Stripe.js');
        }

        return stripe.collectFinancialConnectionsAccounts({
            clientSecret: session.clientSecret,
        });
    }

    /**
     * Link an FC account and sync balance for an external bank account created during KYC.
     * The fcAccountId comes from a completed Financial Connections session.
     */
    public linkExternalAccountBalance(
        organizationId: number,
        ubaId: number,
        fcAccountId: string,
    ): Observable<LinkBalanceResult> {
        const url = Routes.MakeLegFiCoreUrl(
            Routes.TreasuryModule.FinancialConnectionsLinkExternalBalance(organizationId, ubaId),
        );
        return this._http.post<LinkBalanceResult>(url, JSON.stringify({ fcAccountId }));
    }

    /**
     * SD-8605: the whole "Link account" journey for an EXISTING external bank account that has
     * no Financial Connections id — open the Stripe FC modal, then attach the chosen FC account
     * to that same UBA so a balance can be pulled.
     *
     * It never creates a bank account: the backend endpoint updates the UBA identified by
     * `ubaId` in place, so the user's manually-added routing/account record is preserved.
     *
     * Lives on the service (rather than in a component) because two surfaces need the identical
     * journey — the Treasury dashboard bank-balance card and the accounting Transactions
     * totals-box. Emits once on success; completes silently when the user dismisses the modal or
     * picks nothing, so callers can clear their own pending state in `complete`.
     */
    public linkBalanceForExistingAccount(
        organizationId: number,
        ubaId: number,
        callerRendersEmptyResultNotice = false,
    ): Observable<LinkBalanceResult> {
        return this.createSession(organizationId).pipe(
            switchMap((session) => from(this.collectAccounts(session))),
            switchMap((result) => {
                const accounts = result.financialConnectionsSession?.accounts ?? [];

                // A real Stripe error is the only case we can call a failure, so it is the only
                // case that gets an error toast.
                if (result.error) {
                    this._growler.error('Balance not linked', result.error.message);

                    return EMPTY;
                }

                // SD-9078 review (spwoodall): "session present, accounts: [], no error" is
                // AMBIGUOUS: a deliberate close and a manual-entry completion are byte-identical
                // here (see completedWithNoAccounts). The previous revision showed an ERROR growler
                // for this shape, which meant anyone who simply clicked X was told something had
                // failed. That is the exact species of untrue message this ticket exists to remove,
                // and closing the modal is the most common way not to finish it.
                //
                // Unlike addBankAccount() there is no fallback to offer: this journey attaches a
                // BALANCE, and Stripe grants no balance data to a manually entered account. So the
                // response is a WARNING whose copy must be true under EVERY reading; it states only
                // that nothing was linked and what to do differently. It never asserts a failure
                // occurred.
                //
                // SD-9247 adds the THIRD reading, which the original copy did not cover: the
                // institution itself was unreachable. Org 46543's admin reached Primis Bank through
                // Fiserv AIS, which answered "Currently unable to access accounts at this
                // institution" on a page whose only control is Cancel. Cancelling there produces
                // this exact shape, so he was told to "log in to your bank in the Stripe window
                // instead" -- the one action that could not work, and the action he had already
                // taken six times. The institution case is NOT detectable (see
                // completedWithNoAccounts: Stripe emits no cancellation signal and no institution
                // status), so it cannot be branched on; the copy instead names it first and gives
                // the only useful instruction for it, which is to retry later.
                // SD-9557 shortened this. It was three sentences and too long to read in a growler
                // that dismisses itself, so the least useful sentence was dropped rather than the
                // wording being compressed.
                //
                // What went: the manual-entry clause ("if you entered your account numbers manually,
                // reopen this and sign in to your bank in the Stripe window instead"). Two reasons,
                // and the second matters more than the length. It assumed the reader arrived by
                // manual entry, which is not true of SD-9557's From-picker journey — they arrived
                // from a modal that offers only this route. And manual entry on a bare `ba_` mints a
                // PaymentMethod, which drops the row out of the payable path, and stamps a
                // microdeposit hold (proven on prod uba 61782, 2026-08-27). So the sentence pointed
                // some readers at the trap.
                //
                // UPDATED 2026-09-02 — the RECEIVE half of that is no longer true for rows linked
                // after SD-9569/SD-9564 (both 2026-08-28). Core now mints an outbound-only
                // SetupIntent first and stamps `stripe_outbound_setup_intent_id`, and
                // UnifiedBankAccount::getIsOutboundEligibleAttribute() no longer ANDs the hold out
                // when that marker is present — so such a row CAN receive. Rows linked BEFORE it
                // have a NULL marker and are still dead in both directions, which is the cohort the
                // paragraph above was measured on. `getIsTransferEligibleAttribute()` still ANDs the
                // hold out either way, so SENDING still waits on verification.
                //
                // The clause stays removed regardless: this journey is aimed at bare `ba_` rows, and
                // the send direction is what an admin here is usually after.
                //
                // What stays: the honest "nothing happened" statement, and SD-9247's institution
                // case — org 46543's admin hit an unreachable institution and retried six times, and
                // that is the whole reason this branch has copy at all.
                //
                // SD-9731 moved the retry coaching OUT of this growler and onto a persistent
                // alert-block beside the account (`linkFailureNotice`, v2 Treasury dashboard). The
                // constraint is the container, not the wording: a growler is hard-capped at a 5s
                // auto-dismiss (growler.service.ts, autoDismissAfter) with no per-call override,
                // and this branch has four distinct readings to serve.
                //
                // WHICH toast depends on the caller, and the default is the FULL one on purpose.
                // Only that dashboard renders the notice; the accounting totals-box and the SD-9557
                // transfer picker have no persistent surface, so for them this toast is the only
                // channel — including SD-9247's institution case. Trimming unconditionally would
                // delete that guarantee for the two callers that cannot replace it, hence opt-IN.
                //
                // Accepted loss: on the dashboard that institution sentence is not repeated on the
                // card, so it is absent from that one screen. Revisit by consolidating it INTO the
                // alert-block, not by re-expanding this toast.
                //
                // Evidence and decision history: SD-9731.
                if (completedWithNoAccounts(result)) {
                    this._growler.warning(
                        'No balance linked',
                        callerRendersEmptyResultNotice
                            ? 'Nothing was linked and your bank account is unchanged.'
                            : 'Nothing was linked and your bank account is unchanged. If your bank '
                              + 'showed an error page, try again later.',
                    );

                    return EMPTY;
                }

                return this.linkExternalAccountBalance(organizationId, ubaId, accounts[0].id);
            }),
        );
    }

    /**
     * Add a bank account — picks the right flow based on the Treasury feature flag
     * and the org's Connect verification status.
     *
     * Flow selection:
     * - Feature flag OFF → onStandardDialog; caller owns the legacy dialog. onComplete does NOT fire.
     * - Feature flag ON + Connect status 'verified' → opens Stripe Financial Connections modal.
     * - Feature flag ON + Connect status 'pending' / 'restricted' → explains why the action is
     *   unavailable and ends (SD-8351); these orgs have nothing to re-verify, so the KYC flow would
     *   only ask them to redo completed or unhelpful work.
     * - Feature flag ON + Connect status 'not_started' / 'incomplete' → opens the KYC dialog (v1),
     *   then the Stripe Connect onboarding dialog (v1) if the user elects to verify.
     *
     * Callback contract:
     * - onStandardDialog: fires only on the non-Treasury path; caller opens the legacy dialog.
     * - onFcSuccess: fires after a bank account is successfully linked (verified path only), by
     *   EITHER route — Financial Connections or the SD-9078 manual-entry recovery. It receives the
     *   id of the linked UnifiedBankAccount (the first, when Financial Connections returned
     *   several) so a caller can select the account the user just added; SD-8671 added the
     *   argument for the transfer page, which needs to drop the new account straight into the
     *   From/To picker the user was filling in. Callers that do not care may ignore it — a
     *   zero-argument callback still satisfies this signature.
     * - onComplete: fires once when the Treasury flow (FC or KYC) ends, regardless of outcome.
     *   Safe to reset loading state here. Does NOT fire on the onStandardDialog path.
     */
    public addBankAccount(
        organizationId: number,
        onStandardDialog: () => void,
        onFcSuccess: (linkedAccountId?: number) => void,
        onComplete: () => void,
    ): void {
        if (!this._bankAccountInsights.hasTreasuryEnabled()) {
            onStandardDialog();
            return;
        }

        this._treasuryService.connectStatus$.pipe(
            filter((status) => status !== null),
            take(1),
            switchMap((status) => {
                // SD-8351: an org that is PENDING has already completed KYC and is waiting on
                // Stripe's review, and a RESTRICTED org is told by the dashboard banner to contact
                // support. Re-opening the "Start Verification" flow for either one asks them to
                // redo work that is either already done or cannot help — so only the statuses that
                // genuinely have verification left to do (not_started / incomplete) reach the KYC
                // flow. Enforced HERE, at the shared seam, because the accounting Transactions
                // totals-box calls this same method (transactions.component.ts) and would
                // otherwise keep re-prompting even once the dashboard button is disabled.
                if (status === ONBOARDING_STATUSES.PENDING || status === ONBOARDING_STATUSES.RESTRICTED) {
                    const copy = TREASURY_VERIFICATION_BLOCKED_COPY[status];
                    this._growler.warning(copy.title, copy.message);
                    onComplete();

                    return EMPTY;
                }

                if (status !== ONBOARDING_STATUSES.VERIFIED) {
                    this._openKycFlow(organizationId, onComplete);
                    return EMPTY;
                }

                return this.createSession(organizationId).pipe(
                    switchMap((session) => from(this.collectAccounts(session))),
                    switchMap((result) => {
                        const accounts = result.financialConnectionsSession?.accounts ?? [];

                        // SD-9078: BOTH of these used to `return EMPTY`, silently. That is why
                        // org 46825 completed the Stripe modal 32 times, saw Stripe's own "your
                        // account was connected", and got nothing: no bank card, no error, and no
                        // request ever reaching PayHOA. Stripe returns no linked account when the
                        // user finishes by typing their routing and account numbers, so "no
                        // account" is NOT the same as "user cancelled" and must not be treated
                        // as such.
                        //
                        // Review note (spwoodall): the converse is not detectable either. A
                        // deliberate close reaches this branch with the SAME payload as manual
                        // entry (see completedWithNoAccounts), so offering the bank-details dialog
                        // DOES also fire for someone who clicked X. That is accepted here rather
                        // than guarded, because the guard that claimed to prevent it keyed on a
                        // Stripe error code that does not exist and never fired. Of the two
                        // reachable behaviours this is the recoverable one: dismissing the dialog
                        // is silent and costs one Escape, whereas suppressing it returns org 46825
                        // to typing its numbers into a void. An unwanted dialog is a smaller harm
                        // than a swallowed account.
                        if (result.error || completedWithNoAccounts(result)) {
                            return this._collectManualEntry(
                                organizationId,
                                onFcSuccess,
                                result.error?.message,
                            );
                        }

                        const accountIds = accounts.map((account: any) => account.id);
                        return this.processAccounts(organizationId, accountIds);
                    }),
                    catchError((error) => {
                        this._growler.fromErrorResponse(error);
                        return EMPTY;
                    }),
                    finalize(() => onComplete()),
                );
            }),
        ).subscribe({
            next: (result) => {
                if (result) {
                    const count = result.accounts.length;
                    this._growler.success(
                        'Account linked',
                        count === 1
                            ? 'Bank account linked successfully.'
                            : `${count} bank accounts linked successfully.`,
                    );
                    onFcSuccess(result.accounts[0]?.id);
                }
            },
        });
    }

    /**
     * SD-9078: recover the "Stripe gave us no linked account" outcome instead of dying quietly.
     *
     * Reached when the Financial Connections modal reports an error, or completes with an empty
     * account list because the user chose "enter details manually". Reuses the existing
     * ManualAccountEntryComponent (Plaid lookup opted out) to collect the numbers, then hands them
     * to the backend, which mints the Stripe PaymentMethod.
     *
     * Returns EMPTY in every case and reports its own outcome, so the caller's `next` handler
     * keeps dealing only with the Financial Connections response shape.
     *
     * SD-9731 fronts the copy with the retry that can actually produce a linked account, and
     * corrects the transfers claim PER DIRECTION rather than denying it outright.
     *
     * Since SD-9569/SD-9564, linkManualBankAccount() mints an outbound-only SetupIntent, so an
     * account added here CAN receive payouts immediately.
     *
     * SD-9613 settled the other direction, and the previous version of this paragraph is now the
     * trap it warned about. It said the account "cannot SEND until verification completes", which
     * described a wait that no longer exists: core stopped performing the inbound attach, so no hold
     * is stamped, no deposits are sent, and nothing can complete. The account cannot send at all —
     * not yet, but as its finished shape — and the route to sending is connecting the same bank
     * through Stripe, which adopts the row and gives it a Financial Connections id.
     *
     * The instruction it closed with still stands, so keep it: both an unqualified promise and a
     * blanket denial have shipped here before and both were wrong. Check
     * getIsTransferEligibleAttribute() AND isReceiveOnly() in core before editing this sentence
     * again — the hold is no longer what decides it.
     *
     * It also drops the failure claim this string opened with ("could not be linked automatically").
     * The most common way to reach this dialog is the customer closing the Stripe window, for whom
     * nothing was attempted. Stripe's own message still surfaces via `dialogError` on a real error.
     */
    private _collectManualEntry(
        organizationId: number,
        onFcSuccess: (linkedAccountId?: number) => void,
        stripeError?: string,
    ): Observable<never> {
        const data: ManualEntryDialogInput = {
            dialogTitle: 'Enter your bank account details',
            dialogContent: 'No account was linked automatically. If the Stripe window '
                + 'showed a list of previously saved accounts, close this and start again, '
                + 'choosing "Connect a new account" to sign in to your bank directly. A saved '
                + 'entry can fail to re-share without reporting an error. Otherwise, enter the '
                // SD-9613: the last clause used to promise "Stripe must verify them with two small
                // deposits before you can send money from them". Core stopped requesting the inbound
                // direction on a manual add, so no deposits are sent and there is nothing to wait
                // for — the account is finished the moment it is added, in the receive direction.
                // Leaving the old wording would have an admin watching their bank for deposits that
                // are never coming.
                + 'routing and account numbers below to finish connecting it. Accounts added this '
                + 'way can receive payouts straight away. They will not show a balance or import '
                + 'transactions, and you cannot send money FROM them — to do that, connect the same '
                + 'account through your bank instead.',
            dialogError: stripeError,
            verifyPlaid: false,
        };

        return this._dialog.open(ManualAccountEntryComponent, {
            width: MatDialogSizes.MD,
            maxWidth: MatDialogSizes.MD,
            data,
        }).afterClosed().pipe(
            switchMap((entry: ManualEntryDialogResult | undefined) => {
                // A dismissed dialog IS a real cancellation, so this is the one branch that is
                // allowed to stay quiet.
                if (!entry?.success || !entry.routingNumber || !entry.accountNumber) {
                    return EMPTY;
                }

                return this.linkManualAccount(organizationId, {
                    routingNumber: entry.routingNumber,
                    accountNumber: entry.accountNumber,
                    accountHolderType: 'company',
                });
            }),
            tap((response: LinkManualAccountResponse) => {
                if (response.account.microdepositVerificationRequiredAt) {
                    // Never claim the account is ready when Stripe has said it is not — that is
                    // the same false-success this ticket exists to remove.
                    this._growler.success(
                        'Account added',
                        'Stripe is sending two small deposits to this account. Confirm them to '
                        + 'finish verifying it before transferring.',
                    );
                } else {
                    // SD-9613: this is now the branch a manual add ALWAYS takes, so it has to say
                    // what the admin actually got rather than a bare success. The account is usable
                    // immediately as a payout destination and cannot be used to send money — telling
                    // them here is what stops them looking for it in the From list and finding it
                    // greyed out with no explanation of why.
                    //
                    // The held branch above is deliberately kept. It is unreachable from this flow
                    // against current core, but it is the honest answer during a deploy window where
                    // ui is ahead of core, and for any older row this response could describe.
                    this._growler.success(
                        'Account added',
                        'You can send payouts to this account right away. To send money FROM it, '
                        + 'connect the same account through your bank.',
                    );
                }

                onFcSuccess(response.account.id);
            }),
            catchError((error) => {
                this._growler.fromErrorResponse(error);
                return EMPTY;
            }),
            switchMap(() => EMPTY),
        );
    }

    /**
     * Open the KYC / onboarding flow for a Treasury-enabled org that has not yet
     * completed verification. Always shows the v1 KYC intro dialog first, then —
     * on "Start Verification" — opens the Stripe Connect onboarding dialog. Fires
     * onComplete when the flow ends.
     */
    private _openKycFlow(organizationId: number, onComplete: () => void): void {
        this._dialog.open(BankAccountKycDialogComponent, {
            panelClass: 'legacy-kyc-dialog',
            width: MatDialogSizes.MD,
            maxWidth: MatDialogSizes.MD,
            disableClose: true,
            autoFocus: true,
        }).afterClosed().subscribe({
            next: (result: BankAccountKycDialogResult) => {
                if (result?.startVerification) {
                    this._openStripeOnboardingDialog(organizationId, onComplete);
                } else {
                    onComplete();
                }
            },
        });
    }

    private _openStripeOnboardingDialog(organizationId: number, onComplete: () => void): void {
        // SD-8102: gate mandatory-2FA users before v1 Connect onboarding (parity with
        // the v2 KYC interception). TREASURY_KYC is a soft gate — dismissing abandons setup.
        // SD-8330: keyed on mfaEnforced, not mfaRequired — pre-verification mfaRequired is false; this
        // admin-only path is where the enroll-before-KYC prompt belongs.
        if (LegFiJwtService.read()?.mfaEnforced) {
            TwoFactorGateDialogComponent.open(this._gateDialog, TwoFactorGateReason.TREASURY_KYC)
                .afterClosed()
                .pipe(take(1))
                .subscribe((enrolled) => {
                    if (enrolled === true) {
                        this._launchStripeOnboardingDialog(organizationId, onComplete);
                    } else {
                        onComplete();
                    }
                });

            return;
        }

        this._launchStripeOnboardingDialog(organizationId, onComplete);
    }

    private _launchStripeOnboardingDialog(organizationId: number, onComplete: () => void): void {
        const data: BankAccountStripeOnboardingDialogInput = { organizationId };
        this._dialog.open(BankAccountStripeOnboardingDialogComponent, {
            width: BankAccountStripeOnboardingDialogComponent.width,
            maxWidth: BankAccountStripeOnboardingDialogComponent.width,
            disableClose: true,
            autoFocus: true,
            data,
        }).afterClosed().subscribe(() => {
            // Stripe's embedded onboarding has no success/cancel signal — `setOnExit`
            // fires either way. Post-KYC live updates are now broadcast-driven
            // (SD-6865 broadcast adoption):
            //   - `.treasury.connect-status.updated` flips `connectStatus$` to
            //     verified once `account.updated` lands with capability=active.
            //   - `.treasury.financial-account.ready` triggers the bank-accounts
            //     refresh once outbound_transfers features become active.
            // Both events are dispatched from `TreasuryWebhookSubscriber`, so no
            // local orchestration (refresh + poll) is needed here.
            onComplete();
        });
    }
}
